/**
 * Globe 3D Viewer — CesiumJS-based 3D atmospheric visualization.
 * Phase 1 MVP: Globe with terrain + surface pollutant raster + wind particles.
 * NOTE: window.CESIUM_BASE_URL is set in globe.html before this module loads.
 */

import 'cesium/Build/Cesium/Widgets/widgets.css';
import { urlStore, sursaCurenta, monteazaComutator } from './data-source.js';
import {
  Viewer, Ion, Cartesian3, Math as CesiumMath,
  ImageryLayer, SingleTileImageryProvider,
  UrlTemplateImageryProvider,
  ScreenSpaceEventHandler, ScreenSpaceEventType,
  defined, Cartographic, Rectangle,
  Color, Entity, VerticalOrigin, HorizontalOrigin,
  LabelStyle, Cartesian2, Credit,
  CesiumTerrainProvider, Transforms, Matrix4, HeadingPitchRange,
  BoundingSphere,
} from 'cesium';

import * as zarr from 'zarrita';

import { VolumeSlicer } from './slice.js';
import { Wind3DField } from './wind3d.js';
import { VolumeCloud } from './volume-cloud.js';
import { IsoSurface } from './isosurface.js';
import { timesToDates, findClosestTimeIndex } from './data.js';
import {
  tempColorRGBA, tempGradientCss, TEMP_RANGE,
  humidityColorRGBA, humidityGradientCss, HUMIDITY_RANGE,
} from './field-palettes.js';
import { windColorRGBA, WIND_COLORS } from './wind-palette.js';
import {
  valueToColor, AQI_CATEGORIES,
  THRESHOLDS as VAR_THRESHOLDS, UNITS as VAR_UNITS, LABELS as VAR_LABELS,
} from './colormaps.js';

// ── Configuration ──────────────────────────────────────────────────
const BASE = import.meta.env.BASE_URL || '/cams-explorer/';
// Store-urile de poluanți urmează sursa aleasă; cele meteo vin din ECMWF
// în ambele cazuri, deci rămân neschimbate.
const CAMS_ZARR_URL  = urlStore('surface');
const CAMS_3D_ZARR_URL = urlStore('volume');
const WEATHER_ZARR_URL = `${window.location.origin}${BASE}data/zarr/weather_ro_latest.zarr`;
const WEATHER_PL_ZARR_URL = `${window.location.origin}${BASE}data/zarr/weather_ro_pl_latest.zarr`;
const TERRAIN_URL = `${window.location.origin}${BASE}terrain/`;

// Straturile meteo vin dintr-un alt store decât poluanții, cu pas 3-orar.
const WEATHER_VARS = new Set(['temp2m', 'wind_speed', 'cloud', 'precip']);
const isWeatherVar = v => WEATHER_VARS.has(v);

// Thresholds per pollutant (upper bounds for each band)
const THRESHOLDS = {
  pm25: [10, 20, 25, 50, 75, 150],
  pm10: [20, 40, 50, 100, 150, 300],
  no2:  [40, 90, 120, 230, 340, 500],
  o3:   [50, 100, 130, 240, 380, 600],
  so2:  [100, 200, 350, 500, 750, 1250],
  co:   [2000, 4000, 7000, 10000, 20000, 40000],
};

const POLLUTANT_LABELS = {
  pm25: 'PM2.5 — Particule fine',
  pm10: 'PM10 — Particule',
  no2:  'NO₂ — Dioxid de azot',
  o3:   'O₃ — Ozon',
  so2:  'SO₂ — Dioxid de sulf',
  co:   'CO — Monoxid de carbon',
};

const POLLUTANT_UNITS = {
  pm25: 'µg/m³', pm10: 'µg/m³', no2: 'µg/m³',
  o3: 'µg/m³', so2: 'µg/m³', co: 'µg/m³',
};

// City markers
const CITIES = [
  { name: 'București', lat: 44.4268, lon: 26.1025 },
  { name: 'Cluj-Napoca', lat: 46.7712, lon: 23.6236 },
  { name: 'Timișoara', lat: 45.7489, lon: 21.2087 },
  { name: 'Iași', lat: 47.1585, lon: 27.6014 },
  { name: 'Constanța', lat: 44.1598, lon: 28.6348 },
  { name: 'Brașov', lat: 45.6427, lon: 25.5887 },
  { name: 'Sibiu', lat: 45.7983, lon: 24.1256 },
  { name: 'Craiova', lat: 44.3302, lon: 23.7949 },
];

// ── State ──────────────────────────────────────────────────────────
let viewer;
let currentVariable = 'pm25';
let currentTimeIndex = 0;
let currentDates = null;      // Date[] decodat din axa de timp CAMS
let isPlaying = false;
let playInterval = null;
let playSpeed = 1;            // 1× / 2× / 4×, ca în harta 2D

// Data caches
let camsData = {};      // { variable: { data, lats, lons, times, shape } }
let weatherData = {};   // idem, pentru straturile meteo de suprafață

// Raster overlay
let rasterImageryLayer = null;
let rasterCanvas = null;

// Vânt 3D
let wind3d = null;
let windEnabled = false;

// Nor volumetric
let volumeCloud = null;
let volumeEnabled = false;

// Izosuprafață
let isoSurface = null;
let isoEnabled = false;
let isoRange = null;   // { min, max } din cubul curent, pentru cursorul de prag

// Vertical slices
let slicer = null;
let slice3DVars = [];      // variabilele care există în volumul 3D
let sliceDrawMode = false;
let sliceStart = null;     // primul capăt, în modul de desenare

// ── Initialize ─────────────────────────────────────────────────────
async function init() {
  // CesiumJS viewer — no Ion token needed; use OpenStreetMap tiles
  Ion.defaultAccessToken = undefined;

  // Esri World Imagery (satellite)
  const esriSatellite = new UrlTemplateImageryProvider({
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    credit: new Credit('Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics', false),
    maximumLevel: 19,
  });
  
  viewer = new Viewer('cesiumContainer', {
    animation: false,
    timeline: false,
    baseLayerPicker: false,
    fullscreenButton: false,
    geocoder: false,
    homeButton: false,
    infoBox: false,
    selectionIndicator: false,
    navigationHelpButton: false,
    sceneModePicker: false,
    vrButton: false,
    shadows: false,
    shouldAnimate: false,
    imageryProvider: false,       // disable default Bing
    baseLayer: false,             // no auto base layer
  });

  // Add satellite basemap (grayscale)
  const baseLayer = viewer.imageryLayers.addImageryProvider(esriSatellite);
  baseLayer.saturation = 0.0;

  // Globe settings
  viewer.scene.globe.enableLighting = false;
  viewer.scene.backgroundColor = Color.fromCssColorString('#0a0e1a');
  viewer.scene.globe.baseColor = Color.fromCssColorString('#1a2332');
  viewer.scene.fog.enabled = false;
  if (viewer.scene.skyAtmosphere) viewer.scene.skyAtmosphere.show = false;
  if (viewer.scene.skyBox) viewer.scene.skyBox.show = false;
  if (viewer.scene.sun) viewer.scene.sun.show = false;
  if (viewer.scene.moon) viewer.scene.moon.show = false;

  // Anti-flicker: render only when needed (camera move, data change, etc.)
  viewer.scene.requestRenderMode = true;
  viewer.scene.maximumRenderTimeChange = Infinity;

  // Prevent rendering errors from crashing the viewer
  viewer.scene.renderError.addEventListener((scene, error) => {
    console.warn('CesiumJS render error (suppressed):', error);
  });

  // Camera to Romania — centered, looking straight down
  viewer.camera.flyTo({
    destination: Cartesian3.fromDegrees(25.0, 46.0, 1200000),
    orientation: {
      heading: CesiumMath.toRadians(0),
      pitch: CesiumMath.toRadians(-90),
      roll: 0,
    },
    duration: 0,
  });

  // Teren FABDEM (quantized-mesh). Acoperă doar România — în afara zonei
  // Cesium revine automat la elipsoid.
  try {
    viewer.terrainProvider = await CesiumTerrainProvider.fromUrl(TERRAIN_URL, {
      requestVertexNormals: true,
    });
    viewer.scene.globe.enableLighting = true;
  } catch (err) {
    console.warn('Teren FABDEM indisponibil — rămânem pe elipsoid:', err);
  }

  // Add city labels
  addCityLabels();

  // Setup UI (before data loads)
  setupControls();
  setupTimeline();
  // Comutatorul se montează singur doar dacă a doua sursă chiar e publicată.
  monteazaComutator(document.getElementById('sursa-date'));
  setupNavControls();

  // Hide loading overlay — globe is now visible
  const overlay = document.getElementById('loading-overlay');
  overlay.classList.add('fade-out');
  setTimeout(() => overlay.remove(), 600);

  // Load data asynchronously (non-blocking)
  try {
    await loadData();
    renderRaster();
    updateInfoPanel();
  } catch (err) {
    console.warn('Date CAMS indisponibile — globul funcționează fără overlay date:', err);
  }

  // Volumele 3D sunt store-uri separate — o lipsă aici nu afectează rasterul.
  initSlicer().catch(err =>
    console.warn('Volumele 3D indisponibile — secțiunile sunt dezactivate:', err)
  );
  initWind3D().catch(err => {
    console.warn('Vântul 3D indisponibil:', err);
    document.getElementById('btn-wind').style.display = 'none';
  });

  if (import.meta.env.DEV) {
    // Inspecție din consolă, doar în dev. `cesium()` dă acces la modul, al
    // cărui specificator bare nu poate fi rezolvat direct din consolă.
    window.__globe = {
      viewer,
      cesium: () => import('cesium'),
      get slicer() { return slicer; },
      get wind3d() { return wind3d; },
      state: () => ({ currentDates, currentTimeIndex, currentVariable, windEnabled }),
    };
  }
}

// ── Load Zarr Data ─────────────────────────────────────────────────
async function loadData() {
  try {
    const store = new zarr.FetchStore(CAMS_ZARR_URL);
    const root = await zarr.open(store, { kind: 'group' });

    // Load default variable + coordinates
    const arr = await zarr.open(root.resolve(currentVariable), { kind: 'array' });
    const latArr = await zarr.open(root.resolve('latitude'), { kind: 'array' });
    const lonArr = await zarr.open(root.resolve('longitude'), { kind: 'array' });
    const timeArr = await zarr.open(root.resolve('time'), { kind: 'array' });

    const [data, lats, lons, times] = await Promise.all([
      zarr.get(arr), zarr.get(latArr), zarr.get(lonArr), zarr.get(timeArr),
    ]);

    camsData[currentVariable] = {
      data: data.data, lats: lats.data, lons: lons.data,
      times: times.data, shape: data.shape,
      timeAttrs: timeArr.attrs || {},
    };

    // Store metadata
    camsData._meta = {
      lats: lats.data, lons: lons.data, times: times.data,
      timeAttrs: timeArr.attrs || {},
      storeAttrs: root.attrs,
    };

    // Axa de timp decodată + poziționare pe momentul curent, ca în harta 2D:
    // deschiderea hărții pe pasul 0 al prognozei ar arăta trecutul.
    currentDates = timesToDates(times.data, timeArr.attrs || {});
    currentTimeIndex = findClosestTimeIndex(currentDates);

    const slider = document.getElementById('timeline-slider');
    slider.max = data.shape[0] - 1;
    slider.value = currentTimeIndex;
    updateTimeLabel();

    // Try loading wind data

  } catch (err) {
    console.warn('Eroare încărcare date CAMS:', err);
  }
}

/**
 * Deschide store-ul meteo de suprafață și citește o variabilă.
 *
 * E un store separat de cel CAMS, cu grilă mai grosieră (0.25°) și pas 3-orar,
 * deci nu poate fi îmbinat cu el; îl ținem în paralel.
 */
async function loadWeatherVariable(variable) {
  if (weatherData[variable]) return;

  const store = new zarr.FetchStore(WEATHER_ZARR_URL);
  const root = await zarr.open(store, { kind: 'group' });
  const arr = await zarr.open(root.resolve(variable), { kind: 'array' });

  if (!weatherData._meta) {
    const [latArr, lonArr, timeArr] = await Promise.all([
      zarr.open(root.resolve('latitude'), { kind: 'array' }),
      zarr.open(root.resolve('longitude'), { kind: 'array' }),
      zarr.open(root.resolve('time'), { kind: 'array' }),
    ]);
    const [lats, lons, times] = await Promise.all([
      zarr.get(latArr), zarr.get(lonArr), zarr.get(timeArr),
    ]);
    weatherData._meta = { lats: lats.data, lons: lons.data, times: times.data };
  }

  const data = await zarr.get(arr);
  weatherData[variable] = {
    data: data.data, shape: data.shape,
    lats: weatherData._meta.lats,
    lons: weatherData._meta.lons,
    times: weatherData._meta.times,
  };
}

/** Cubul din care se desenează rasterul curent. */
function activeRasterCube() {
  return isWeatherVar(currentVariable)
    ? weatherData[currentVariable] : camsData[currentVariable];
}

/** Pasul de timp din cubul activ: CAMS e orar, meteo e 3-orar. */
function activeRasterTimeIndex() {
  if (!isWeatherVar(currentVariable)) return currentTimeIndex;
  const n = weatherData._meta?.times?.length ?? 33;
  return Math.min(Math.round(currentTimeIndex / 3), n - 1);
}

async function loadVariableIfNeeded(variable) {
  if (isWeatherVar(variable)) return loadWeatherVariable(variable);
  if (camsData[variable]) return;
  try {
    const store = new zarr.FetchStore(CAMS_ZARR_URL);
    const root = await zarr.open(store, { kind: 'group' });
    const arr = await zarr.open(root.resolve(variable), { kind: 'array' });
    const data = await zarr.get(arr);

    camsData[variable] = {
      data: data.data, shape: data.shape,
      lats: camsData._meta.lats,
      lons: camsData._meta.lons,
      times: camsData._meta.times,
      timeAttrs: camsData._meta.timeAttrs,
    };
  } catch (err) {
    console.warn(`Nu s-a putut încărca variabila ${variable}:`, err);
  }
}

// ── Color Mapping ──────────────────────────────────────────────────
/**
 * Poziția valorii pe scara de benzi EEA, 0–1.
 *
 * Pragurile din THRESHOLDS sunt marginile benzilor de calitate, nu o scară
 * liniară. Raportarea directă la ultimul prag comprimă tot spectrul util în
 * prima bandă — 25 µg/m³ PM2.5, adică „Moderat", ar ieși verde.
 */
function bandPosition(value, variable) {
  const th = THRESHOLDS[variable] || THRESHOLDS.pm25;
  const n = th.length;
  if (!(value > 0)) return 0;
  if (value >= th[n - 1]) return 1;

  let i = 0;
  while (i < n && value > th[i]) i++;
  const lo = i === 0 ? 0 : th[i - 1];
  const hi = th[i];
  return (i + (value - lo) / (hi - lo)) / n;
}

/**
 * Culoarea rasterului drapat pe teren.
 *
 * Culorile vin din `colormaps.js`, aceleași ca în harta 2D. Diferă doar
 * opacitatea: pe glob rasterul stă peste relief, care trebuie să rămână
 * vizibil, așa că valorile mici sunt translucide.
 */
function valueToRGBA(value, variable) {
  if (value == null || isNaN(value)) return [0, 0, 0, 0];
  const [r, g, b] = valueToColor(value, variable);

  let alpha;
  if (variable === 'temp2m') {
    // Câmp semnificativ peste tot — nu are sens „temperatură puțină".
    alpha = 0.78;
  } else if (variable === 'wind_speed') {
    alpha = 0.72;
  } else if (variable === 'cloud' || variable === 'precip') {
    // Aici zero chiar înseamnă „nimic de arătat".
    const th = VAR_THRESHOLDS[variable];
    const t = Math.min(Math.max(value / th[th.length - 1], 0), 1);
    alpha = Math.min(0.10 + t * 2.4, 0.88);
  } else {
    alpha = Math.min(Math.max(bandPosition(value, variable) * 1.5, 0.15), 0.85);
  }
  return [r, g, b, Math.round(alpha * 255)];
}

/**
 * Opacitate pentru secțiuni verticale.
 *
 * Rasterul de suprafață poate fi aproape transparent fiindcă se sprijină
 * vizual pe hartă; o perdea suspendată în aer dispare dacă e la fel de
 * palidă. Pornim de la o bază care se citește ca suprafață și lăsăm
 * concentrația să o închidă până aproape de opac.
 */
function valueToCurtainRGBA(value, variable) {
  if (value == null || isNaN(value)) return [0, 0, 0, 0];
  const [r, g, b] = valueToColor(value, variable);
  const pos = bandPosition(value, variable);
  const alpha = Math.min(0.22 + 0.73 * Math.pow(pos, 0.6), 0.97);
  return [r, g, b, Math.round(alpha * 255)];
}

// ── Raster Rendering ───────────────────────────────────────────────
async function renderRaster() {
  const cube = activeRasterCube();
  if (!cube) return;

  const { data, lats, lons, shape } = cube;
  const [nt, nlat, nlon] = shape;
  const ti = Math.min(activeRasterTimeIndex(), nt - 1);

  // Create canvas for raster
  if (!rasterCanvas) {
    rasterCanvas = document.createElement('canvas');
  }
  rasterCanvas.width = nlon;
  rasterCanvas.height = nlat;

  const ctx = rasterCanvas.getContext('2d');
  const imgData = ctx.createImageData(nlon, nlat);
  const pixels = imgData.data;

  // Fill pixels — note: lats are south→north, canvas is top→bottom
  for (let y = 0; y < nlat; y++) {
    const latIdx = nlat - 1 - y; // flip vertically
    for (let x = 0; x < nlon; x++) {
      const value = data[ti * nlat * nlon + latIdx * nlon + x];
      const [r, g, b, a] = valueToRGBA(value, currentVariable);
      const pi = (y * nlon + x) * 4;
      pixels[pi]     = r;
      pixels[pi + 1] = g;
      pixels[pi + 2] = b;
      pixels[pi + 3] = a;
    }
  }
  ctx.putImageData(imgData, 0, 0);

  // Upscale for smoother rendering with bilinear interpolation
  const upCanvas = document.createElement('canvas');
  const upScale = 4;
  upCanvas.width = nlon * upScale;
  upCanvas.height = nlat * upScale;
  const upCtx = upCanvas.getContext('2d');
  upCtx.imageSmoothingEnabled = true;
  upCtx.imageSmoothingQuality = 'high';
  upCtx.drawImage(rasterCanvas, 0, 0, upCanvas.width, upCanvas.height);

  // Create imagery from canvas
  const dataUrl = upCanvas.toDataURL('image/png');

  // Bounds from zarr metadata
  const west = Number(lons[0]);
  const east = Number(lons[lons.length - 1]);
  const south = Number(lats[0]);
  const north = Number(lats[lats.length - 1]);

  // Remove old layer
  if (rasterImageryLayer) {
    viewer.imageryLayers.remove(rasterImageryLayer, true);
    rasterImageryLayer = null;
  }

  // Add new layer with proper rectangle (fromUrl is required in CesiumJS 1.104+)
  try {
    const imgProvider = await SingleTileImageryProvider.fromUrl(dataUrl, {
      rectangle: Rectangle.fromDegrees(west, south, east, north),
    });
    rasterImageryLayer = viewer.imageryLayers.addImageryProvider(imgProvider);
    rasterImageryLayer.alpha = 0.75;
  } catch (err) {
    console.warn('Eroare la crearea stratului raster:', err);
  }

  // Trigger repaint in requestRenderMode
  viewer.scene.requestRender();
}

// ── Câmpurile care se pot felia ────────────────────────────────────
/**
 * Viteza vântului pe o secțiune.
 *
 * Paleta e aceeași ca la particule, dar opacitatea urcă cu viteza: aerul lin
 * lasă să se vadă ce e dincolo, curentul-jet iese în față.
 */
function windSpeedCurtainRGBA(speed) {
  if (speed == null || isNaN(speed)) return [0, 0, 0, 0];
  const [r, g, b] = windColorRGBA(speed);
  const a = Math.min(0.35 + 0.6 * Math.min(speed / 30, 1), 0.95);
  return [Math.round(r), Math.round(g), Math.round(b), Math.round(a * 255)];
}

/** Rampa CSS a paletei de vânt, pentru legendă. */
function windGradientCss() {
  const max = WIND_COLORS[WIND_COLORS.length - 1].speed;
  const stops = WIND_COLORS.map(s => {
    const c = s.color;
    return `rgb(${c[0]},${c[1]},${c[2]}) ${(100 * s.speed / max).toFixed(1)}%`;
  });
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

const AQI_GRADIENT = `linear-gradient(90deg, ${AQI_CATEGORIES.map(
  (c, i) => `${c.color} ${(100 * i / (AQI_CATEGORIES.length - 1)).toFixed(0)}%`
).join(', ')})`;

/**
 * Câmpurile disponibile pentru secțiuni.
 *
 * Doar mărimile cu structură verticală ajung aici. Nebulozitatea și
 * precipitațiile sunt câmpuri de suprafață — nu au ce felie să dea.
 */
const FIELDS_3D = {
  pm25: {
    source: 'cams', variable: 'pm25', label: 'PM2.5', unit: 'µg/m³',
    color: v => valueToCurtainRGBA(v, 'pm25'), gradient: () => AQI_GRADIENT,
    fixedRange: { min: 0, max: THRESHOLDS.pm25[THRESHOLDS.pm25.length - 1] },
  },
  no2: {
    source: 'cams', variable: 'no2', label: 'NO₂', unit: 'µg/m³',
    color: v => valueToCurtainRGBA(v, 'no2'), gradient: () => AQI_GRADIENT,
    fixedRange: { min: 0, max: THRESHOLDS.no2[THRESHOLDS.no2.length - 1] },
  },
  temp: {
    source: 'weather', variable: 'temp', label: 'Temperatură', unit: '°C',
    color: tempColorRGBA, gradient: tempGradientCss,
    fixedRange: TEMP_RANGE,
  },
  wind_speed: {
    source: 'weather', variable: 'wind_speed', label: 'Viteza vântului', unit: 'm/s',
    color: windSpeedCurtainRGBA, gradient: windGradientCss,
    fixedRange: { min: 0, max: WIND_COLORS[WIND_COLORS.length - 1].speed },
  },
  humidity: {
    source: 'weather', variable: 'humidity', label: 'Umiditate relativă', unit: '%',
    color: humidityColorRGBA, gradient: humidityGradientCss,
    fixedRange: HUMIDITY_RANGE,
  },
};

let field3dKey = 'pm25';
let available3dFields = [];

// ── Secțiuni verticale ─────────────────────────────────────────────
/**
 * Cere randarea mai multor cadre consecutive.
 *
 * Cu `requestRenderMode` activ, tot ce se pregătește asincron — texturi de
 * material, reteselarea terenului la schimbarea exagerării — are nevoie de
 * câteva cadre ca să ajungă pe ecran.
 */
function requestFrames(frames = 8) {
  let remaining = frames;
  const tick = () => {
    viewer.scene.requestRender();
    if (--remaining > 0) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

async function initSlicer() {
  const s = new VolumeSlicer(viewer);

  // Sursele au axe verticale diferite: CAMS în metri deasupra solului, meteo
  // pe nivele de presiune, cu altitudinea reală din înălțimea geopotențială.
  const added = [];
  try {
    await s.addSource('cams', { url: CAMS_3D_ZARR_URL });
    added.push('cams');
  } catch (err) {
    console.warn('Volumele CAMS 3D indisponibile:', err);
  }
  try {
    await s.addSource('weather', { url: WEATHER_PL_ZARR_URL, altitudeVar: 'geoheight' });
    added.push('weather');
  } catch (err) {
    console.warn('Volumele meteo 3D indisponibile:', err);
  }
  if (added.length === 0) return;

  for (const [key, f] of Object.entries(FIELDS_3D)) {
    if (added.includes(f.source) && await s.hasVariable(f.source, f.variable)) {
      available3dFields.push(key);
    }
  }
  if (available3dFields.length === 0) return;

  slice3DVars = available3dFields
    .filter(k => FIELDS_3D[k].source === 'cams')
    .map(k => FIELDS_3D[k].variable);

  slicer = s;
  field3dKey = available3dFields.includes(currentVariable)
    ? currentVariable : available3dFields[0];
  applyField3D(field3dKey);

  volumeCloud = new VolumeCloud(viewer);
  isoSurface = new IsoSurface(viewer);

  document.getElementById('slice-group').style.display = 'flex';
  setupSliceControls();
  setupFieldSelector();
  setupVolumeControls();
  setupIsoControls();
}

/**
 * Reconstruiește izosuprafața pentru pragul curent.
 *
 * Pragul se dă normalizat 0–1000 și se mapează pe domeniul real al cubului:
 * altfel un cursor în unități absolute ar fi inutilizabil, fiindcă PM2.5 și
 * umiditatea diferă cu trei ordine de mărime.
 */
let isoBusy = false;
async function refreshIso() {
  if (!isoEnabled || !isoSurface || !slicer?.available || isoBusy) return;
  isoBusy = true;
  try {
    slicer.timeIndex = sliceTimeIndex();
    const cube = await slicer.cubeFor(slicer.timeIndex);

    let vmin = Infinity, vmax = -Infinity;
    for (let i = 0; i < cube.data.length; i++) {
      const v = cube.data[i];
      if (v < vmin) vmin = v;
      if (v > vmax) vmax = v;
    }
    isoRange = { min: vmin, max: vmax };

    const raw = parseInt(document.getElementById('iso-threshold-slider').value, 10);
    const iso = vmin + (vmax - vmin) * raw / 1000;

    const field = FIELDS_3D[field3dKey];
    const rgba = field.color(iso);
    isoSurface.color = [rgba[0], rgba[1], rgba[2]];
    isoSurface.exaggeration = slicer.exaggeration;

    const t0 = performance.now();
    const tris = isoSurface.rebuild(cube, {
      lats: slicer.coords.lats,
      lons: slicer.coords.lons,
      altitudes: slicer.altitudes,
    }, iso);
    const ms = Math.round(performance.now() - t0);

    const fmt = Math.abs(iso) >= 100 ? iso.toFixed(0) : iso.toFixed(1);
    document.getElementById('iso-threshold-value').textContent = `${fmt} ${field.unit}`;
    document.getElementById('iso-count').textContent =
      tris > 0 ? `${(tris / 1000).toFixed(1)}k triunghiuri · ${ms} ms`
               : 'niciun punct peste prag';
    requestFrames(6);
  } catch (err) {
    console.warn('Eroare la construirea izosuprafeței:', err);
  } finally {
    isoBusy = false;
  }
}

function setupIsoControls() {
  const btn = document.getElementById('btn-iso');
  const group = document.getElementById('iso-group');
  const thr = document.getElementById('iso-threshold-slider');
  const op = document.getElementById('iso-opacity-slider');

  btn.addEventListener('click', async () => {
    isoEnabled = !isoEnabled;
    btn.classList.toggle('active', isoEnabled);
    group.style.display = isoEnabled ? 'block' : 'none';
    if (isoEnabled) {
      await refreshIso();
      isoSurface.show();
    } else {
      isoSurface.hide();
    }
  });

  // Reconstruirea costă sute de milisecunde, deci o facem la eliberare,
  // nu la fiecare pixel de glisare.
  thr.addEventListener('input', () => {
    if (!isoRange) return;
    const raw = parseInt(thr.value, 10);
    const v = isoRange.min + (isoRange.max - isoRange.min) * raw / 1000;
    const unit = FIELDS_3D[field3dKey].unit;
    document.getElementById('iso-threshold-value').textContent =
      `${Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1)} ${unit}`;
  });
  thr.addEventListener('change', () => refreshIso());

  op.addEventListener('input', () => {
    const v = parseInt(op.value, 10);
    document.getElementById('iso-opacity-value').textContent = `${v}%`;
    isoSurface.opacity = v / 100;
    requestFrames(4);
  });
}

/**
 * Reconstruiește norul volumetric din câmpul 3D curent.
 *
 * Geometria e statică între schimbări, deci reconstruim doar la comutarea
 * câmpului, a pasului de timp sau a scării verticale — nu în fiecare cadru.
 */
let volumeBusy = false;
async function refreshVolume() {
  if (!volumeEnabled || !volumeCloud || !slicer?.available || volumeBusy) return;
  volumeBusy = true;
  try {
    slicer.timeIndex = sliceTimeIndex();
    const cube = await slicer.cubeFor(slicer.timeIndex);
    const grid = {
      lats: slicer.coords.lats,
      lons: slicer.coords.lons,
      altitudes: slicer.altitudes,
    };
    volumeCloud.exaggeration = slicer.exaggeration;
    const n = volumeCloud.rebuild(cube, grid, FIELDS_3D[field3dKey].color);
    document.getElementById('volume-count').textContent =
      `${(n / 1000).toFixed(0)}k puncte`;
    requestFrames(6);
  } catch (err) {
    console.warn('Eroare la construirea norului volumetric:', err);
  } finally {
    volumeBusy = false;
  }
}

function setupVolumeControls() {
  const btn = document.getElementById('btn-volume');
  const group = document.getElementById('volume-group');

  btn.addEventListener('click', async () => {
    volumeEnabled = !volumeEnabled;
    btn.classList.toggle('active', volumeEnabled);
    group.style.display = volumeEnabled ? 'block' : 'none';
    if (volumeEnabled) {
      await refreshVolume();
      volumeCloud.show();
    } else {
      volumeCloud.hide();
    }
  });

  const bind = (sliderId, valueId, format, apply, rebuild) => {
    const slider = document.getElementById(sliderId);
    const value = document.getElementById(valueId);
    slider.addEventListener('input', () => {
      const raw = parseInt(slider.value, 10);
      value.textContent = format(raw);
      apply(raw);
      if (!rebuild) requestFrames(4);
    });
    if (rebuild) slider.addEventListener('change', () => refreshVolume());
    value.textContent = format(parseInt(slider.value, 10));
    apply(parseInt(slider.value, 10));
  };

  bind('volume-opacity-slider', 'volume-opacity-value',
    v => `${v}%`, v => { volumeCloud.opacity = v / 100; }, true);
  bind('volume-size-slider', 'volume-size-value',
    v => String(v), v => { volumeCloud.pointSize = v; }, false);
  bind('volume-density-slider', 'volume-density-value',
    v => `${v}×`, v => { volumeCloud.samplesPerCell = v; }, true);
}

/** Comută câmpul afișat de felii și actualizează legenda. */
function applyField3D(key) {
  const f = FIELDS_3D[key];
  field3dKey = key;
  slicer.setField(f.source, f.variable, f.color);
  document.getElementById('field3d-bar').style.background = f.gradient();
  updateFieldLegend();
}

/**
 * Capetele legendei.
 *
 * Scara e fixă per câmp, nu întinsă pe datele pasului curent: altfel aceeași
 * culoare ar însemna altceva la fiecare oră, iar bara de gradient (care
 * desenează toată paleta) n-ar corespunde etichetelor.
 */
function updateFieldLegend() {
  const f = FIELDS_3D[field3dKey];
  const r = f.fixedRange;
  const fmt = v => (Math.abs(v) >= 100 ? String(Math.round(v)) : v.toFixed(0));
  document.getElementById('field3d-min').textContent = fmt(r.min);
  document.getElementById('field3d-max').textContent = `${fmt(r.max)} ${f.unit}`;
}

function setupFieldSelector() {
  const sel = document.getElementById('field3d-select');
  sel.innerHTML = '';
  for (const key of available3dFields) {
    const opt = document.createElement('option');
    opt.value = key;
    opt.textContent = FIELDS_3D[key].label;
    sel.appendChild(opt);
  }
  sel.value = field3dKey;
  document.getElementById('field3d-row').style.display = 'flex';

  sel.addEventListener('change', async () => {
    applyField3D(sel.value);
    // Sursele au număr diferit de pași; reașezăm indexul înainte de redesenare.
    slicer.timeIndex = sliceTimeIndex();
    await slicer.refresh();
    if (slicer.levelAlt !== null) requestLevel(slicer.levelAlt, false);
    updateFieldLegend();
    updateCoverageNote();
    refreshVolume();
    refreshIso();
    if (windEnabled && tracerMode === 'field') applyTracerMode();
  });
}

/** Rasterul e orar, volumul 3D e 3-orar — mapăm indexul dintre ele. */
function sliceTimeIndex() {
  return Math.min(Math.round(currentTimeIndex / 3), slicer.timeSteps - 1);
}

let sliceRefreshing = false;
async function refreshSlices() {
  if (!slicer || slicer.count === 0 || sliceRefreshing) return;
  sliceRefreshing = true;
  try {
    slicer.timeIndex = sliceTimeIndex();
    await slicer.refresh();
    updateFieldLegend();
    refreshVolume();
    refreshIso();
  } catch (err) {
    console.warn('Eroare la redesenarea secțiunilor:', err);
  } finally {
    sliceRefreshing = false;
  }
}

/**
 * Privire oblică dinspre sud-vest, încadrând întreg domeniul.
 *
 * De la nadir o felie verticală e invizibilă, iar din sud cea nord–sud se vede
 * din muchie; unghiul diagonal le arată pe amândouă.
 *
 * Poziționarea se face pe o sferă de încadrare, nu pe coordonate fixe cu pitch
 * fix: la 900 km altitudine și −25°, punctul vizat cădea la ~1900 km în față,
 * adică mult în afara zonei de interes.
 */
function flyOblique() {
  const b = slicer.bounds;
  const top = slicer.topAltitude * slicer.exaggeration;

  const center = Cartesian3.fromDegrees(
    (b.west + b.east) / 2, (b.south + b.north) / 2, top / 2
  );
  const corner = Cartesian3.fromDegrees(b.west, b.south, top);
  const radius = Cartesian3.distance(center, corner);

  viewer.camera.flyToBoundingSphere(new BoundingSphere(center, radius), {
    offset: new HeadingPitchRange(
      CesiumMath.toRadians(35), CesiumMath.toRadians(-30), radius * 2.2
    ),
    duration: 1.5,
  });
  requestFrames(120);
}

// ── Poziționarea planelor de secțiune ──────────────────────────────
// Mutările vin din două surse (tragerea mânerului și cursoarele X/Y) și pot
// sosi mai des decât reușim să redesenăm. Le trecem printr-o singură coadă cu
// un element, ca redesenarea să se auto-limiteze la ritmul ei real.
let crossPending = null;
let crossBusy = false;

function requestCross(lon, lat, fast = true) {
  crossPending = { lon, lat, fast };
  pumpCross();
}

function pumpCross() {
  if (crossBusy || !crossPending || !slicer) return;
  crossBusy = true;
  const { lon, lat, fast } = crossPending;
  crossPending = null;
  slicer.setCross(lon, lat, { fast })
    .catch(err => console.warn('Eroare la mutarea secțiunii:', err))
    .finally(() => { crossBusy = false; syncAxisSliders(); pumpCross(); });
}

let levelPending = null;
let levelBusy = false;

function requestLevel(altM, fast = true) {
  levelPending = { altM, fast };
  pumpLevel();
}

function pumpLevel() {
  if (levelBusy || !levelPending || !slicer) return;
  levelBusy = true;
  const { altM, fast } = levelPending;
  levelPending = null;
  const done = () => { levelBusy = false; pumpLevel(); };
  if (altM === null) { slicer.hideLevel(); done(); return; }
  slicer.setLevel(altM, { fast })
    .catch(err => console.warn('Eroare la felia orizontală:', err))
    .finally(done);
}

const sliderToValue = (el, lo, hi) =>
  lo + (hi - lo) * (parseInt(el.value, 10) / 1000);
const valueToSlider = (v, lo, hi) =>
  Math.round(1000 * (v - lo) / (hi - lo));

/** Ține cursoarele X/Y în pas cu tragerea mânerului. */
function syncAxisSliders() {
  const c = slicer?.crossCenter;
  if (!c) return;
  const b = slicer.bounds;
  const xs = document.getElementById('slice-x-slider');
  const ys = document.getElementById('slice-y-slider');
  xs.value = valueToSlider(c.lon, b.west, b.east);
  ys.value = valueToSlider(c.lat, b.south, b.north);
  document.getElementById('slice-x-value').textContent = `${c.lon.toFixed(2)}°E`;
  document.getElementById('slice-y-value').textContent = `${c.lat.toFixed(2)}°N`;
}

function setupAxisControls() {
  const b = slicer.bounds;
  const xs = document.getElementById('slice-x-slider');
  const ys = document.getElementById('slice-y-slider');
  const zs = document.getElementById('slice-z-slider');
  const os = document.getElementById('slice-opacity-slider');

  const moveCross = (fast) => {
    const lon = sliderToValue(xs, b.west, b.east);
    const lat = sliderToValue(ys, b.south, b.north);
    document.getElementById('slice-x-value').textContent = `${lon.toFixed(2)}°E`;
    document.getElementById('slice-y-value').textContent = `${lat.toFixed(2)}°N`;
    requestCross(lon, lat, fast);
  };
  for (const el of [xs, ys]) {
    el.addEventListener('input', () => moveCross(true));
    el.addEventListener('change', () => moveCross(false));
  }

  const moveLevel = (fast) => {
    const raw = parseInt(zs.value, 10);
    const label = document.getElementById('slice-z-value');
    if (raw === 0) {
      label.textContent = 'oprit';
      requestLevel(null);
      return;
    }
    const alt = slicer.topAltitude * raw / 1000;
    label.textContent = alt >= 1000
      ? `${(alt / 1000).toFixed(1)} km` : `${Math.round(alt)} m`;
    requestLevel(alt, fast);
  };
  zs.addEventListener('input', () => moveLevel(true));
  zs.addEventListener('change', () => moveLevel(false));

  os.addEventListener('input', () => {
    const v = parseInt(os.value, 10);
    document.getElementById('slice-opacity-value').textContent = `${v}%`;
    slicer.opacity = v / 100;
  });
  os.addEventListener('change', () => {
    slicer.refresh();
    if (slicer.levelAlt !== null) requestLevel(slicer.levelAlt, false);
  });
}

function setupSliceControls() {
  const btnCross = document.getElementById('btn-slice-cross');
  const btnDraw = document.getElementById('btn-slice-draw');
  const btnClear = document.getElementById('btn-slice-clear');
  const exagSlider = document.getElementById('exag-slider');
  const exagValue = document.getElementById('exag-value');
  const hint = document.getElementById('slice-hint');

  btnCross.addEventListener('click', async () => {
    const b = slicer.bounds;
    slicer.clear();
    slicer.timeIndex = sliceTimeIndex();
    await slicer.setCross((b.west + b.east) / 2, (b.south + b.north) / 2);

    btnCross.classList.add('active');
    document.getElementById('slice-axes').style.display = 'block';
    syncAxisSliders();

    hint.textContent = 'Trage de punctul din centru sau folosește cursoarele X / Y / Z';
    hint.classList.add('visible');
    setTimeout(() => hint.classList.remove('visible'), 5000);
    flyOblique();
  });

  btnDraw.addEventListener('click', () => {
    sliceDrawMode = !sliceDrawMode;
    sliceStart = null;
    btnDraw.classList.toggle('active', sliceDrawMode);
    hint.classList.toggle('visible', sliceDrawMode);
    hint.textContent = 'Click pe hartă pentru primul capăt al secțiunii';
  });

  btnClear.addEventListener('click', () => {
    slicer.clear();
    sliceDrawMode = false;
    sliceStart = null;
    btnCross.classList.remove('active');
    btnDraw.classList.remove('active');
    hint.classList.remove('visible');
    document.getElementById('slice-axes').style.display = 'none';
    const zs = document.getElementById('slice-z-slider');
    zs.value = 0;
    document.getElementById('slice-z-value').textContent = 'oprit';
  });

  setupAxisControls();

  // Terenul, perdelele și particulele trebuie să folosească exact aceeași
  // scară verticală: baza feliei e calculată ca teren × exagerare, deci dacă
  // globul rămâne la 1× perdeaua plutește deasupra reliefului real.
  const applyExaggeration = (ex) => {
    exagValue.textContent = `${ex}×`;
    slicer.exaggeration = ex;
    viewer.scene.verticalExaggeration = ex;
    if (wind3d) {
      wind3d.exaggeration = ex;
      wind3d.refreshExaggeration();
    }
    if (volumeEnabled) refreshVolume();
    if (isoEnabled) refreshIso();
    // Cesium retesela tile-urile de teren pe parcursul mai multor cadre; cu
    // requestRenderMode activ, un singur requestRender lasă relieful la
    // scara veche și perdeaua pare că plutește.
    requestFrames(90);
  };

  applyExaggeration(parseInt(exagSlider.value, 10));
  exagSlider.addEventListener('input', () => {
    applyExaggeration(parseInt(exagSlider.value, 10));
  });
  exagSlider.addEventListener('change', () => refreshSlices());

  // Desenarea feliei: două click-uri pe glob
  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
  handler.setInputAction(async (movement) => {
    if (!sliceDrawMode) return;

    const cartesian = viewer.camera.pickEllipsoid(
      movement.position, viewer.scene.globe.ellipsoid
    );
    if (!defined(cartesian)) return;

    const carto = Cartographic.fromCartesian(cartesian);
    const point = {
      lon: CesiumMath.toDegrees(carto.longitude),
      lat: CesiumMath.toDegrees(carto.latitude),
    };

    if (!sliceStart) {
      sliceStart = point;
      hint.textContent = 'Click pentru celălalt capăt';
      return;
    }

    slicer.timeIndex = sliceTimeIndex();
    await slicer.addCurtain(sliceStart, point);
    sliceStart = null;
    sliceDrawMode = false;
    document.getElementById('btn-slice-draw').classList.remove('active');
    hint.classList.remove('visible');
    flyOblique();
  }, ScreenSpaceEventType.LEFT_CLICK);

  setupCrossDrag();
}

/**
 * Mutarea interactivă a crucii de secțiuni.
 *
 * Redesenarea completă a unei felii costă ~120 ms, aproape integral din
 * `sampleTerrainMostDetailed`. În timpul tragerii folosim regimul rapid al
 * slicer-ului (textură la jumătate, profil de teren din cache, fără etichete),
 * iar la eliberare recompunem la calitate completă.
 */
function setupCrossDrag() {
  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
  const canvas = viewer.scene.canvas;

  let dragging = false;

  /** Punctul de pe relief sub cursor. */
  const pickGround = (windowPos) => {
    const ray = viewer.camera.getPickRay(windowPos);
    let pos = ray ? viewer.scene.globe.pick(ray, viewer.scene) : undefined;
    if (!defined(pos)) {
      pos = viewer.camera.pickEllipsoid(windowPos, viewer.scene.globe.ellipsoid);
    }
    if (!defined(pos)) return null;
    const c = Cartographic.fromCartesian(pos);
    return {
      lon: CesiumMath.toDegrees(c.longitude),
      lat: CesiumMath.toDegrees(c.latitude),
    };
  };

  handler.setInputAction((e) => {
    if (!slicer?.handle) return;
    const picked = viewer.scene.pick(e.position);
    if (!picked || picked.id !== slicer.handle) return;

    dragging = true;
    // Altfel glisarea ar roti și camera în același timp.
    viewer.scene.screenSpaceCameraController.enableInputs = false;
    canvas.style.cursor = 'grabbing';
  }, ScreenSpaceEventType.LEFT_DOWN);

  handler.setInputAction((movement) => {
    if (!dragging) {
      // Semnalăm că mânerul se poate apuca.
      if (slicer?.handle) {
        const picked = viewer.scene.pick(movement.endPosition);
        canvas.style.cursor = picked?.id === slicer.handle ? 'grab' : '';
      }
      return;
    }
    const p = pickGround(movement.endPosition);
    if (p) requestCross(p.lon, p.lat, true);
  }, ScreenSpaceEventType.MOUSE_MOVE);

  handler.setInputAction(() => {
    if (!dragging) return;
    dragging = false;
    viewer.scene.screenSpaceCameraController.enableInputs = true;
    canvas.style.cursor = '';

    const c = slicer?.crossCenter;
    if (c) requestCross(c.lon, c.lat, false);   // recompunere la calitate completă
  }, ScreenSpaceEventType.LEFT_UP);
}

// ── Vânt 3D ────────────────────────────────────────────────────────
async function initWind3D() {
  const field = new Wind3DField(viewer, { url: WEATHER_PL_ZARR_URL });
  await field.load();
  wind3d = field;

  const exag = parseInt(document.getElementById('exag-slider').value, 10);
  wind3d.exaggeration = exag;

  document.getElementById('btn-wind').style.display = 'flex';
  setupWindControls();
  updateCoverageNote();
}

/**
 * Comută particulele între „arată vântul" și „arată transportul câmpului".
 *
 * În al doilea caz vântul rămâne cel care le mișcă — se schimbă doar unde se
 * seamănă (ponderat de concentrație) și cum se colorează. Așa se vede încotro
 * duce vântul poluantul, nu doar unde e el acum.
 */
let tracerMode = 'wind';

async function applyTracerMode() {
  if (!wind3d?.available) return;

  if (tracerMode !== 'field' || !slicer?.available) {
    wind3d.setScalarSource(null);
    return;
  }
  try {
    slicer.timeIndex = sliceTimeIndex();
    const cube = await slicer.cubeFor(slicer.timeIndex);

    let vmin = Infinity, vmax = -Infinity;
    for (let i = 0; i < cube.data.length; i++) {
      const v = cube.data[i];
      if (v < vmin) vmin = v;
      if (v > vmax) vmax = v;
    }

    // Culoarea vine din paleta câmpului, dar opacitatea nu: cea a perdelelor
    // e calibrată pentru o pânză plină și lasă liniile subțiri aproape
    // invizibile. Trasorii primesc propria rampă, ca dârele de vânt.
    const base = FIELDS_3D[field3dKey].color;
    const span = Math.max(1e-9, vmax - vmin);
    const colorFn = (v) => {
      const c = base(v);
      const norm = Math.max(0, Math.min(1, (v - vmin) / span));
      const a = Math.min(0.45 + 0.55 * Math.sqrt(norm), 1);
      return [c[0], c[1], c[2], Math.round(a * 255)];
    };

    wind3d.setScalarSource({
      cube,
      lats: slicer.coords.lats,
      lons: slicer.coords.lons,
      altitudes: slicer.altitudes,
      colorFn,
      vmin, vmax,
    });
  } catch (err) {
    console.warn('Nu s-a putut lega câmpul de particule:', err);
    wind3d.setScalarSource(null);
  }
}

function setupWindControls() {
  const base = wind3d.speedFactor;

  document.getElementById('tracer-select').addEventListener('change', async (e) => {
    tracerMode = e.target.value;
    await applyTracerMode();
    viewer.scene.requestRender();
  });

  const bind = (sliderId, valueId, format, apply) => {
    const slider = document.getElementById(sliderId);
    const value = document.getElementById(valueId);
    const handler = () => {
      const raw = parseInt(slider.value, 10);
      value.textContent = format(raw);
      apply(raw);
      viewer.scene.requestRender();
    };
    slider.addEventListener('input', handler);
    handler();
  };

  bind('wind-speed-slider', 'wind-speed-value',
    v => `${(v / 100).toFixed(1)}×`,
    v => { wind3d.speedFactor = base * v / 100; });

  bind('wind-length-slider', 'wind-length-value',
    v => String(v),
    v => { wind3d.trailStretch = v; });

  bind('wind-density-slider', 'wind-density-value',
    v => `${v}%`,
    v => wind3d.setDensity(v / 100));

  bind('wind-opacity-slider', 'wind-opacity-value',
    v => `${v}%`,
    v => { wind3d.opacity = v / 100; });
}

/**
 * Indexul din volumul de vânt corespunzător orei curente.
 *
 * Rasterul CAMS e orar și merge până la +96h, dar vântul pe nivele de presiune
 * e 3-orar și se oprește la +48h (vezi ECMWF_PL_STEPS din pipeline).
 */
function windTimeIndex() {
  return Math.min(Math.round(currentTimeIndex / 3), wind3d.timeSteps - 1);
}

/** Ora maximă acoperită de datele de vânt. */
function windMaxHour() {
  return (wind3d.timeSteps - 1) * 3;
}

async function syncWindTime() {
  if (!wind3d?.available) return;
  try {
    await wind3d.setTime(windTimeIndex());
    // Trasorii poartă câmpul pasului curent, nu pe cel de la pornire.
    if (tracerMode === 'field') await applyTracerMode();
  } catch (err) {
    console.warn('Nu s-a putut încărca pasul de vânt:', err);
  }
  updateCoverageNote();
}

/**
 * Semnalează când datele afișate nu mai corespund orei alese.
 *
 * CAMS merge până la +96h, dar câmpurile meteo pe nivele de presiune se opresc
 * la +48h. Fără avertisment, dincolo de limită s-ar afișa tăcut ultimul pas.
 */
function updateCoverageNote() {
  const note = document.getElementById('wind-note');
  if (!note) return;

  const maxH = wind3d?.available ? windMaxHour() : 48;
  const peste = currentTimeIndex > maxH;
  const vantInvechit = peste && windEnabled && wind3d?.available;
  const feliiInvechite = peste && slicer?.available
    && FIELDS_3D[field3dKey]?.source === 'weather' && slicer.count > 0;

  const ce = [];
  if (vantInvechit) ce.push('vântul 3D');
  if (feliiInvechite) ce.push('secțiunile meteo');

  const stale = ce.length > 0;
  note.textContent = stale
    ? `Datele meteo acoperă doar +${maxH}h — ${ce.join(' și ')} arată ultimul pas disponibil`
    : '';
  note.classList.toggle('visible', stale);
}

// ── Navigare 3D ────────────────────────────────────────────────────
// Pe trackpad, înclinarea nativă Cesium cere Ctrl + glisare, ceea ce nu e
// descoperibil. Butoanele de mai jos fac aceleași mișcări explicit.

const TILT_STEP = CesiumMath.toRadians(6);
const ROT_STEP = CesiumMath.toRadians(8);
const PITCH_MIN = CesiumMath.toRadians(-89);   // aproape de nadir
const PITCH_MAX = CesiumMath.toRadians(-2);    // aproape de orizont

/** Punctul de pe teren din centrul ecranului — pivotul rotațiilor. */
function pivotPoint() {
  const canvas = viewer.scene.canvas;
  const center = new Cartesian2(canvas.clientWidth / 2, canvas.clientHeight / 2);
  let p = viewer.scene.pickPosition(center);
  if (!defined(p)) {
    p = viewer.camera.pickEllipsoid(center, viewer.scene.globe.ellipsoid);
  }
  return p;
}

/**
 * Orbitează în jurul punctului din centrul ecranului.
 *
 * Citim unghiurile curente față de pivot și le impunem pe cele noi, în loc să
 * aplicăm rotații incrementale: `rotateUp`/`rotateDown` urmate de o anulare
 * la depășirea limitelor se blocau după primul pas, fiindcă pivotul se mută
 * odată cu camera și unghiul citit nu mai corespundea mișcării cerute.
 *
 * @param {number} headingDelta  radiani, pozitiv spre dreapta
 * @param {number} pitchDelta    radiani, pozitiv spre orizontală
 */
function orbitAroundCenter(headingDelta, pitchDelta) {
  const pivot = pivotPoint();
  if (!defined(pivot)) return;

  const cam = viewer.camera;
  cam.lookAtTransform(Transforms.eastNorthUpToFixedFrame(pivot));
  const range = Cartesian3.magnitude(cam.position);
  const heading = cam.heading + headingDelta;
  const pitch = CesiumMath.clamp(cam.pitch + pitchDelta, PITCH_MIN, PITCH_MAX);
  cam.lookAtTransform(Matrix4.IDENTITY);

  cam.lookAt(pivot, new HeadingPitchRange(heading, pitch, range));
  cam.lookAtTransform(Matrix4.IDENTITY);
  requestFrames(10);
}

/** @param {boolean} towardVertical  true = spre nadir, false = spre orizont */
function tiltCamera(towardVertical) {
  orbitAroundCenter(0, towardVertical ? -TILT_STEP : TILT_STEP);
}

function rotateCamera(clockwise) {
  orbitAroundCenter(clockwise ? ROT_STEP : -ROT_STEP, 0);
}

function zoomCamera(inward) {
  // Pasul proporțional cu altitudinea: la 1000 km sărim altfel decât la 10 km.
  const height = viewer.camera.positionCartographic.height;
  const step = Math.max(500, height * 0.3);
  if (inward) viewer.camera.zoomIn(step);
  else viewer.camera.zoomOut(step);
  requestFrames(10);
}

function resetView() {
  if (slicer?.available && slicer.count > 0) {
    flyOblique();
  } else {
    viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(25.0, 44.6, 700000),
      orientation: {
        heading: CesiumMath.toRadians(0),
        pitch: CesiumMath.toRadians(-45),
        roll: 0,
      },
      duration: 1.2,
    });
  }
  requestFrames(60);
}

function setupNavControls() {
  const bind = (id, fn) => document.getElementById(id).addEventListener('click', fn);
  bind('nav-tilt-up', () => tiltCamera(true));
  bind('nav-tilt-down', () => tiltCamera(false));
  bind('nav-rot-left', () => rotateCamera(false));
  bind('nav-rot-right', () => rotateCamera(true));
  bind('nav-zoom-in', () => zoomCamera(true));
  bind('nav-zoom-out', () => zoomCamera(false));
  bind('nav-reset', resetView);

  document.addEventListener('keydown', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    switch (e.key.toLowerCase()) {
      case 'w': tiltCamera(true); break;
      case 's': tiltCamera(false); break;
      case 'a': rotateCamera(false); break;
      case 'd': rotateCamera(true); break;
      case 'r': resetView(); break;
      case '+': case '=': zoomCamera(true); break;
      case '-': case '_': zoomCamera(false); break;
      default: return;
    }
    e.preventDefault();
  });
}

// ── City Labels ────────────────────────────────────────────────────
function addCityLabels() {
  CITIES.forEach(city => {
    viewer.entities.add({
      position: Cartesian3.fromDegrees(city.lon, city.lat, 100),
      label: {
        text: city.name,
        font: '12px Inter, sans-serif',
        fillColor: Color.fromCssColorString('#e2e8f0'),
        outlineColor: Color.fromCssColorString('#0a0e1a'),
        outlineWidth: 3,
        style: LabelStyle.FILL_AND_OUTLINE,
        verticalOrigin: VerticalOrigin.BOTTOM,
        pixelOffset: new Cartesian2(0, -8),
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
        scaleByDistance: undefined,
      },
      point: {
        pixelSize: 5,
        color: Color.fromCssColorString('#00d2ff'),
        outlineColor: Color.fromCssColorString('#0a0e1a'),
        outlineWidth: 2,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      },
    });
  });
}

// ── UI: Controls ───────────────────────────────────────────────────
function setupControls() {
  const panel = document.getElementById('controls-panel');
  panel.addEventListener('click', async (e) => {
    // Butoanele de secțiune folosesc aceeași clasă, dar n-au data-var —
    // fără garda asta ar reseta selecția de poluant.
    const btn = e.target.closest('.layer-btn[data-var]');
    if (!btn) return;

    const varName = btn.dataset.var;

    // Un singur strat de bază activ, ca în harta 2D.
    panel.querySelectorAll('.layer-btn[data-var]').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');

    currentVariable = varName;
    await loadVariableIfNeeded(varName);
    renderRaster();
    updateInfoPanel();

    // Doar o parte din poluanți au volum 3D; ceilalți lasă secțiunile neatinse.
    if (slicer && available3dFields.includes(varName)) {
      applyField3D(varName);
      const sel = document.getElementById('field3d-select');
      if (sel) sel.value = varName;
      refreshSlices();
    }
  });

  // Particulele 3D sunt overlay, nu strat de bază: rămân active peste orice
  // alt strat, exact ca „Overlay vânt" din harta 2D.
  document.getElementById('wind-overlay-checkbox')
    .addEventListener('change', async (e) => {
      if (!wind3d?.available) { e.target.checked = false; return; }
      windEnabled = e.target.checked;
      document.getElementById('wind-group').style.display = windEnabled ? 'block' : 'none';
      if (windEnabled) {
        await syncWindTime();
        wind3d.enable();
      } else {
        wind3d.disable();
      }
      updateCoverageNote();
    });
}

// ── UI: Timeline ───────────────────────────────────────────────────
const SPEEDS = [1, 2, 4];
const BASE_INTERVAL_MS = 400;

/** Aplică un nou pas de timp în toate straturile. */
function applyTimeIndex(t) {
  const slider = document.getElementById('timeline-slider');
  currentTimeIndex = t;
  slider.value = t;
  renderRaster();
  updateTimeLabel();
  refreshSlices();
  if (windEnabled) syncWindTime();
  updateCoverageNote();
}

function setupTimeline() {
  const slider = document.getElementById('timeline-slider');
  const playBtn = document.getElementById('play-btn');
  const speedBtn = document.getElementById('speed-btn');

  slider.addEventListener('input', () => {
    applyTimeIndex(parseInt(slider.value, 10));
  });

  playBtn.addEventListener('click', togglePlay);

  speedBtn.addEventListener('click', () => {
    playSpeed = SPEEDS[(SPEEDS.indexOf(playSpeed) + 1) % SPEEDS.length];
    speedBtn.textContent = `${playSpeed}×`;
    if (isPlaying) { stopAnimation(); startAnimation(); }
  });

  // Aceleași scurtături ca în harta 2D.
  document.addEventListener('keydown', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT') return;
    const max = parseInt(slider.max, 10);
    switch (e.key) {
      case ' ':
      case 'k':
        e.preventDefault(); togglePlay(); break;
      case 'ArrowRight':
      case 'l':
        e.preventDefault(); applyTimeIndex(Math.min(currentTimeIndex + 1, max)); break;
      case 'ArrowLeft':
      case 'j':
        e.preventDefault(); applyTimeIndex(Math.max(currentTimeIndex - 1, 0)); break;
    }
  });

  updateTimeLabel();
}

function togglePlay() {
  isPlaying = !isPlaying;
  document.getElementById('icon-play').classList.toggle('hidden', isPlaying);
  document.getElementById('icon-pause').classList.toggle('hidden', !isPlaying);
  if (isPlaying) startAnimation();
  else stopAnimation();
}

function startAnimation() {
  stopAnimation();
  const slider = document.getElementById('timeline-slider');
  const max = parseInt(slider.max, 10);
  playInterval = setInterval(() => {
    applyTimeIndex(currentTimeIndex >= max ? 0 : currentTimeIndex + 1);
  }, BASE_INTERVAL_MS / playSpeed);
}

function stopAnimation() {
  if (playInterval) { clearInterval(playInterval); playInterval = null; }
}

const FMT_HOUR = new Intl.DateTimeFormat('ro-RO', {
  timeZone: 'Europe/Bucharest', hour: '2-digit', minute: '2-digit', hour12: false,
});
const FMT_FULL = new Intl.DateTimeFormat('ro-RO', {
  timeZone: 'Europe/Bucharest', weekday: 'short', day: 'numeric', month: 'short',
  hour: '2-digit', minute: '2-digit', hour12: false,
});

function updateTimeLabel() {
  const date = currentDates?.[currentTimeIndex];
  document.getElementById('time-lead').textContent =
    date ? FMT_HOUR.format(date) : '—';
  document.getElementById('time-datetime').textContent =
    date ? FMT_FULL.format(date) : '—';

  const infoStep = document.getElementById('info-step');
  const max = document.getElementById('timeline-slider').max;
  infoStep.textContent = `+${currentTimeIndex}h din ${max}h`;
}

/**
 * Legenda stratului curent.
 *
 * Gradientul se eșantionează din aceeași `valueToColor` folosită la desenare,
 * deci bara arată exact culorile de pe glob — și implicit pe cele din 2D.
 */
function legendGradient(variable) {
  const th = VAR_THRESHOLDS[variable];
  if (!th) return null;
  const lo = th[0], hi = th[th.length - 1];
  const steps = 12;
  const stops = [];
  for (let i = 0; i <= steps; i++) {
    const v = lo + (hi - lo) * i / steps;
    const [r, g, b] = valueToColor(v, variable);
    stops.push(`rgb(${r},${g},${b}) ${(100 * i / steps).toFixed(0)}%`);
  }
  return `linear-gradient(90deg, ${stops.join(', ')})`;
}

function updateInfoPanel() {
  const v = currentVariable;
  const unit = VAR_UNITS[v] || '';

  document.getElementById('info-title').textContent =
    POLLUTANT_LABELS[v] || `${VAR_LABELS[v] || v}${unit ? ` — ${unit}` : ''}`;

  const th = VAR_THRESHOLDS[v] || VAR_THRESHOLDS.pm25;
  const bar = document.getElementById('legend-bar');
  const grad = legendGradient(v);
  if (grad) bar.style.background = grad;

  document.getElementById('legend-min').textContent = th[0];
  document.getElementById('legend-mid').textContent = th[Math.floor(th.length / 2)];
  document.getElementById('legend-max').textContent =
    `${th[th.length - 1]}${v === 'temp2m' ? '' : '+'}`;

  const infoDate = document.getElementById('info-date');
  const meta = camsData._meta?.storeAttrs;
  infoDate.textContent = meta?.run_date || '—';
}

// ── Boot ───────────────────────────────────────────────────────────
init().catch(err => {
  console.error('Eroare inițializare Globe 3D:', err);
  const overlay = document.getElementById('loading-overlay');
  if (overlay) {
    overlay.querySelector('.loading-text').textContent = 'Eroare la încărcare. Verificați consola.';
    overlay.querySelector('.loading-spinner').style.display = 'none';
  }
});
